import { useEffect, useState } from 'react';
import { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { createDeliveryFees } from 'api/cateringApi';

// Distance-based delivery fee is the real, authoritative fee - this only
// falls back to the chef's flat delivery_price_cents/delivery_fee_cents
// when the distance lookup can't be completed (address outside the
// chef's delivery range, the geocoding/backend call failing, etc.), so
// customers always see *a* fee instead of a blocking error, while still
// preferring the more accurate distance-based number whenever it's
// actually available. All values are in cents.
export const resolveDeliveryFeeWithFallback = async (
  chefId,
  address,
  staticFeeCents,
) => {
  if (!address || !chefId) {
    return { deliveryFee: staticFeeCents, isFallback: true };
  }
  try {
    const results = await geocodeByAddress(address);
    const prop = await getLatLng(results[0]);
    const response = await createDeliveryFees({
      chef_id: chefId,
      user_lat: prop.lat,
      user_lng: prop.lng,
      address,
    });
    if (typeof response?.deliveryFee === 'number') {
      return { deliveryFee: response.deliveryFee, isFallback: false };
    }
    return { deliveryFee: staticFeeCents, isFallback: true };
  } catch (e) {
    return { deliveryFee: staticFeeCents, isFallback: true };
  }
};

// Live version of the above for display purposes - starts from the
// static fee immediately (no loading flash) and upgrades to the
// distance-based one once resolved, or stays on the static fee if the
// lookup fails.
export const useDeliveryFee = (chefId, address, staticFeeCents) => {
  const [state, setState] = useState({
    deliveryFee: staticFeeCents,
    loading: false,
    isFallback: true,
  });

  useEffect(() => {
    let cancelled = false;
    setState(prev => ({ ...prev, loading: true }));
    resolveDeliveryFeeWithFallback(chefId, address, staticFeeCents).then(
      result => {
        if (!cancelled) setState({ ...result, loading: false });
      },
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chefId, address, staticFeeCents]);

  return state;
};
